import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SellerAvatar from "@/components/SellerAvatar";
import SpotlightWings from "@/components/beacon/SpotlightWings";
import SpotlightCrown from "@/components/beacon/SpotlightCrown";
import { api } from "@/lib/api";

/**
 * SpotlightConstellation — a living cluster of avatars representing
 * every seller admins have personally chosen for the Beacon Spotlight.
 *
 * Animations layered:
 *   1. **Magnetic Field Drift** — springy random targets, mutual repulsion
 *   2. **Heartbeat Pulse** — whole cluster scales 1.0 ↔ 1.045 every 1.4s
 *   3. **Sparkle Burst** — 10 white sparkles fountain outward from
 *      every avatar like the picture is *exploding* with light. Each
 *      particle has its own delay so the burst is continuous, not
 *      synchronized.
 *
 * The container that bounds the avatars is the SAME element measured
 * by the ResizeObserver — they share `position: relative` + the same
 * absolute-positioned coordinate space, so drift targets are always
 * inside what the audience can see.
 */

// Avatar + wing sizes are RESPONSIVE — smaller on mobile so the
// cluster doesn't dominate the screen, bigger on desktop where there's
// room to breathe. Heading clearance also shrinks on mobile because
// the heading itself drops from text-4xl to text-2xl.
const SIZES = {
  desktop: { base: 116, hot: 140, wingScale: 2.2, headingHeight: 130 },
  mobile:  { base: 56,  hot: 72,  wingScale: 2.0, headingHeight: 84  },
};
const EDGE_BUFFER = 18;
const REPEL_RADIUS = 200;
const REPEL_STRENGTH = 80;

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

function computeGeom(cfg) {
  const wingReach = (cfg.hot * cfg.wingScale) / 2;
  const safePad = wingReach + EDGE_BUFFER;
  const topReserved = cfg.headingHeight + wingReach;
  return { wingReach, safePad, topReserved };
}

function pickRandom(boundsW, boundsH, geom) {
  const { safePad, topReserved } = geom;
  const w = boundsW > 2 * safePad ? boundsW : 2 * safePad + 1;
  const minY = safePad + topReserved;
  const h = boundsH > minY + safePad ? boundsH : minY + safePad + 1;
  return {
    x: safePad + Math.random() * (w - 2 * safePad),
    y: minY + Math.random() * (h - minY - safePad),
  };
}

export default function SpotlightConstellation() {
  const [sellers, setSellers] = useState([]);
  const [bounds, setBounds] = useState({ w: 0, h: 0 });
  // Responsive avatar/wing sizes — small on phones, big on desktop.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const fieldRef = useRef(null);

  // Watch viewport for breakpoint flips (e.g. rotation / desktop resize).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < 640);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const sizeCfg = isMobile ? SIZES.mobile : SIZES.desktop;
  const geom = useMemo(() => computeGeom(sizeCfg), [sizeCfg]);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api
        .get("/beacon/allowed-sellers")
        .then((r) => { if (!cancelled) setSellers(r.data?.items || []); })
        .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Measure the EXACT element that hosts the avatars so the bounds
  // we clamp against == the area the user actually sees. We re-run
  // this whenever `sellers.length` flips from 0→N, because the early
  // `return null` at the top means `fieldRef.current` is null until
  // we have at least one seller to render.
  useEffect(() => {
    if (!fieldRef.current) return;
    const measure = () => {
      const el = fieldRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setBounds({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(fieldRef.current);
    return () => ro.disconnect();
  }, [sellers.length]);

  const [targets, setTargets] = useState({});

  useEffect(() => {
    if (!sellers.length || !bounds.w || !bounds.h) return;
    setTargets((cur) => {
      const next = { ...cur };
      const minY = geom.safePad + geom.topReserved;
      sellers.forEach((s) => {
        if (!next[s.user_id]) {
          next[s.user_id] = { ...pickRandom(bounds.w, bounds.h, geom), tick: 0 };
        } else {
          // Re-clamp cached targets to new bounds (e.g. phone rotated
          // or breakpoint switched between mobile/desktop sizes).
          next[s.user_id] = {
            ...next[s.user_id],
            x: clamp(next[s.user_id].x, geom.safePad, bounds.w - geom.safePad),
            y: clamp(next[s.user_id].y, minY, bounds.h - geom.safePad),
          };
        }
      });
      return next;
    });

    const reroll = () => {
      setTargets((cur) => {
        const next = { ...cur };
        const moveCount = Math.max(1, Math.min(2, Math.ceil(sellers.length / 4)));
        for (let i = 0; i < moveCount; i++) {
          const s = sellers[Math.floor(Math.random() * sellers.length)];
          if (!s) continue;
          let pt = pickRandom(bounds.w, bounds.h, geom);

          for (const other of sellers) {
            if (other.user_id === s.user_id) continue;
            const o = next[other.user_id];
            if (!o) continue;
            const dx = pt.x - o.x;
            const dy = pt.y - o.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < REPEL_RADIUS * REPEL_RADIUS && d2 > 0.01) {
              const d = Math.sqrt(d2);
              const k = (REPEL_RADIUS - d) / REPEL_RADIUS;
              pt = {
                x: pt.x + (dx / d) * REPEL_STRENGTH * k,
                y: pt.y + (dy / d) * REPEL_STRENGTH * k,
              };
            }
          }
          pt.x = clamp(pt.x, geom.safePad, bounds.w - geom.safePad);
          pt.y = clamp(pt.y, geom.safePad + geom.topReserved, bounds.h - geom.safePad);
          next[s.user_id] = { ...pt, tick: (next[s.user_id]?.tick || 0) + 1 };
        }
        return next;
      });
    };
    const t = setInterval(reroll, 3200);
    return () => clearInterval(t);
  }, [sellers, bounds.w, bounds.h, geom]);

  const isHot = useMemo(() => {
    const map = {};
    const now = new Date().toISOString();
    sellers.forEach((s) => {
      map[s.user_id] = s.beacon_boost_until && s.beacon_boost_until > now;
    });
    return map;
  }, [sellers]);

  // Track which Spotlight sellers are CURRENTLY broadcasting live, so
  // we can flare + spin their crown as a "the king has entered the
  // room" cue. Polled every 25s — cheap, no WebSocket dependency.
  const [liveSellerIds, setLiveSellerIds] = useState(() => new Set());
  useEffect(() => {
    if (!sellers.length) return;
    let cancelled = false;
    const pollLive = () =>
      api
        .get("/streams", { params: { status: "live", limit: 200 } })
        .then((r) => {
          if (cancelled) return;
          const items = r.data?.items || r.data || [];
          const ids = new Set(items.map((s) => s.seller_id).filter(Boolean));
          setLiveSellerIds(ids);
        })
        .catch(() => {});
    pollLive();
    const t = setInterval(pollLive, 25_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [sellers.length]);

  if (!sellers.length) return null;

  return (
    <section
      className="relative overflow-hidden rounded-3xl text-white border border-white/10 stage-bless-bg"
      data-testid="spotlight-constellation"
    >
      {/* Aspect ratio is on the wrapper; the inner `fieldRef` div is the
          coordinate space the avatars live in, and it's the exact thing
          ResizeObserver measures. */}
      <div className="relative aspect-square sm:aspect-[16/9]" ref={fieldRef}>
        {/* Subtle dark overlay so the wings + crowns + text still pop
            against the colorful stage backdrop. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 75%)" }}
        />

        {/* WHITE SPARKLES emanating from the two stage spotlight beams in
            the backdrop image. Two clusters of 6 twinkling 4-point stars
            each, positioned at ~37% and ~63% horizontal, ~22% vertical
            (where the spotlight glows are in the source PNG). */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {/* Left spotlight cluster */}
          {[
            { top: "12%", left: "35%", d: "0.0s", dur: "1.4s", sc: 1.5 },
            { top: "18%", left: "38%", d: "0.4s", dur: "1.9s", sc: 1.1 },
            { top: "23%", left: "33%", d: "0.8s", dur: "1.6s", sc: 1.3 },
            { top: "29%", left: "36%", d: "1.2s", dur: "2.1s", sc: 0.9 },
            { top: "34%", left: "39%", d: "0.2s", dur: "1.8s", sc: 1.0 },
            { top: "40%", left: "34%", d: "1.0s", dur: "2.2s", sc: 0.8 },
          ].map((s, i) => (
            <span
              key={`l${i}`}
              className="stage-light-sparkle"
              style={{
                top: s.top, left: s.left,
                animationDelay: s.d,
                animationDuration: s.dur,
                "--sparkle-scale": s.sc,
              }}
            />
          ))}
          {/* Right spotlight cluster */}
          {[
            { top: "12%", left: "63%", d: "0.6s", dur: "1.5s", sc: 1.5 },
            { top: "17%", left: "66%", d: "0.0s", dur: "2.0s", sc: 1.2 },
            { top: "24%", left: "61%", d: "1.1s", dur: "1.7s", sc: 1.3 },
            { top: "30%", left: "64%", d: "0.3s", dur: "2.3s", sc: 0.9 },
            { top: "35%", left: "67%", d: "0.9s", dur: "1.9s", sc: 1.0 },
            { top: "41%", left: "62%", d: "1.4s", dur: "2.1s", sc: 0.8 },
          ].map((s, i) => (
            <span
              key={`r${i}`}
              className="stage-light-sparkle"
              style={{
                top: s.top, left: s.left,
                animationDelay: s.d,
                animationDuration: s.dur,
                "--sparkle-scale": s.sc,
              }}
            />
          ))}
          {/* Animated bright pulse on each spotlight cone — fakes the
              light getting brighter/dimmer in rhythm with the sparkles. */}
          <span className="stage-light-glow stage-light-glow-l" />
          <span className="stage-light-glow stage-light-glow-r" />
        </div>

        {/* Heading overlay (top). Solid contrast background so the
            text never collides with drifting avatars. */}
        <div className="absolute top-0 left-0 right-0 z-30 pointer-events-none px-4 sm:px-6 pt-4 sm:pt-6 pb-3 bg-gradient-to-b from-zinc-950/95 via-zinc-950/70 to-transparent">
          <p className="overline text-amber-300 mb-1.5 flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
            The chosen few
          </p>
          <h3 className="font-display font-black text-2xl sm:text-4xl tracking-tighter">
            Spotlight sellers.
          </h3>
        </div>

        {/* Heartbeat scale wraps the whole field — every avatar
            inherits the synchronized breath. */}
        <motion.div
          className="absolute inset-0"
          animate={{ scale: [1, 1.045, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        >
          {sellers.map((s) => {
            const fallbackX = bounds.w / 2 || 200;
            const fallbackY = bounds.h / 2 || 200;
            const target = targets[s.user_id] || { x: fallbackX, y: fallbackY };
            const hot = isHot[s.user_id];
            const liveNow = liveSellerIds.has(s.user_id);
            return (
              <DriftAvatar
                key={s.user_id}
                seller={s}
                target={target}
                hot={hot}
                liveNow={liveNow}
                sizeCfg={sizeCfg}
              />
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------
   <DriftAvatar /> — one seller. Springs toward `target`, has its own
   per-avatar pulse, wrapped in the Sparkle Burst (10 particles flying
   outward) + photoreal wings + jeweled crown.
   ------------------------------------------------------------------ */
function DriftAvatar({ seller, target, hot, liveNow, sizeCfg }) {
  const size = hot ? sizeCfg.hot : sizeCfg.base;
  const wingScale = sizeCfg.wingScale;

  // 10 particle angles around the avatar, evenly spaced with offset
  // per-particle delay so the burst is continuous (not synchronized).
  const PARTICLES = 10;
  const particles = Array.from({ length: PARTICLES }, (_, i) => ({
    angle: (i / PARTICLES) * 360 + (i % 2 === 0 ? 0 : 18), // alternate slight rotation
    delay: (i * 0.18).toFixed(2),
  }));

  return (
    <motion.div
      className="absolute"
      style={{
        top: 0,
        left: 0,
        translateX: "-50%",
        translateY: "-50%",
      }}
      animate={{ x: target.x, y: target.y }}
      transition={{
        type: "spring",
        stiffness: 35,
        damping: 14,
        mass: 1.4,
      }}
    >
      <motion.div
        animate={{ scale: hot ? [1, 1.12, 1] : [1, 1.04, 1] }}
        transition={{
          duration: hot ? 0.9 : 2.4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <Link
          to={`/seller/${seller.user_id}`}
          data-testid={`constellation-avatar-${seller.user_id}`}
          className="group relative inline-flex flex-col items-center"
        >
          {/* GOLDEN WINGS — photoreal angel wings spreading outward
              behind the avatar. Shifted slightly upward (translateY
              -58% instead of -50%) so the avatar sits LOWER in the
              wing-frame, eliminating empty space between the avatar
              and the username pill below. */}
          <div
            className="absolute top-1/2 left-1/2 pointer-events-none spotlight-wings-wrap"
            style={{
              width: size * wingScale,
              height: size * wingScale,
              transform: "translate(-50%,-58%)",
            }}
            aria-hidden="true"
          >
            <SpotlightWings size={size * wingScale} />
          </div>

          {/* JEWELED CROWN — photoreal 3D gold crown PNG. Sits RIGHT
              ON TOP of the avatar (base of crown overlaps the top
              ~20% of the avatar circle) and is z-stacked ABOVE the
              avatar so it's never hidden behind it. */}
          <div
            className={`absolute left-1/2 pointer-events-none ${liveNow ? "spotlight-crown-flare" : ""}`}
            style={{
              width: size * 0.85,
              height: size * 0.62,
              top: -size * 0.44,
              transform: "translateX(-50%)",
              zIndex: 5,
            }}
            aria-hidden="true"
          >
            <SpotlightCrown size={size * 0.85} />
          </div>

          {/* SPARKLE BURST — 10 white particles fly outward from the
              avatar's center on staggered delays. Looks like the
              picture is bursting with light. */}
          <div
            className="absolute top-1/2 left-1/2 pointer-events-none"
            aria-hidden="true"
            style={{ width: size, height: size, transform: "translate(-50%,-50%)" }}
          >
            {particles.map((p, i) => (
              <span
                key={i}
                className="constellation-burst-particle"
                style={{
                  "--angle": `${p.angle}deg`,
                  "--delay": `${p.delay}s`,
                }}
              />
            ))}
          </div>

          {/* The avatar — clean 3D sphere with specular sheen + rim
              shadow, NO gold halo, NO ring outline. */}
          <div
            className="constellation-avatar-3d relative rounded-full"
            style={{ width: size, height: size }}
          >
            <SellerAvatar
              picture={seller.picture}
              name={seller.username}
              size={size}
              className="block"
            />
            <span aria-hidden="true" className="constellation-avatar-sheen pointer-events-none" />
            <span aria-hidden="true" className="constellation-avatar-rim pointer-events-none" />
          </div>

          {/* Username pill — sits just below the avatar (mt-1) so the
              empty area between the picture and the name is removed. */}
          <span className="relative mt-1 max-w-[120px] truncate text-[10px] sm:text-xs font-bold text-white bg-zinc-950 px-2.5 py-0.5 rounded-full ring-1 ring-white/15 shadow-md whitespace-nowrap">
            @{seller.username}
          </span>
        </Link>
      </motion.div>
    </motion.div>
  );
}
